<template>
  <div :class="$style['home-slider-event']">
    <div :class="$style['event-img']"></div>
    <div
      v-if="hideInfo"
      :class="$style['event-timer']"
    >
      <div :class="$style['timer-wrapper']">
        <IconTimerRound />
        <div :class="$style['time-to-end']">
          {{ event.day }}{{ t("home.banner.banner_days") }} {{ event.hours
          }}{{ t("home.banner.banner_hours") }}
        </div>
      </div>
    </div>
    <div
      :class="$style['event-info']"
      data-test="bullpass-widget-redirect"
      @click="router.push(normalize(`/event`))"
    >
      <div
        v-if="hideInfo"
        :class="$style['lvl-name']"
      >
        <span
          v-if="userIsAuthorized"
          :class="$style['lvl']"
          >{{ event.user.currentLevel }}</span
        >
        <span :class="$style['name']">Bullpass <IconArrow2 /> </span>
      </div>
      <div
        v-if="userIsAuthorized && hideInfo"
        :class="$style['lvl-progress']"
      >
        <div
          v-if="maxLvl"
          :class="$style['max-lvl']"
        >
          <p>
            {{ t("bullpass.max_level") }}
          </p>
        </div>
        <div
          v-else
          :class="$style['not-max-lvl']"
        >
          <span
            >{{ event.user.defensePoints }} / {{ event.user.levelCost }}</span
          >
          <div
            :class="$style['back-progress']"
            :style="{ width: `${currentProgressPercent}%` }"
          ></div>
        </div>
      </div>
      <div
        v-else-if="!userIsAuthorized && hideInfo"
        :class="$style['author-btn']"
        data-test="bullpass-widget-login"
        @click="userStore.showAuth('login')"
      >
        {{ t("auth.log_into_account") }}
      </div>
      <div
        v-if="!hideInfo"
        :class="$style.coming"
      >
        Bullpass is coming...
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { normalize } from "~/utils/url/normalize";
import { useServerDateTime } from "~/composables/date/useServerDateTime";
const { serverDateTime } = useServerDateTime();
const { t } = useI18n();
const router = useRouter();
const platform = usePlatform();
const userStore = useUserStore();
const bullpassStore = useBullpassStore();
const event = computed(() => bullpassStore.event);
const maxLvl = computed(() => {
  if (
    event.value.levels.length === event.value.user.currentLevel &&
    event.value.user.levelCost === null
  ) {
    return true;
  } else return false;
});

const currentProgressPercent = computed(() => {
  if (event.value.user.defensePoints && event.value.user.levelCost) {
    return (event.value.user.defensePoints / event.value.user.levelCost) * 100;
  } else return 0;
});

const userIsAuthorized = computed(() => userStore.userIsAuthorized);

const hideInfo = computed(() => {
  return !(platform.is.genshin || platform.is.roblox || platform.is.free_fire);
});

const timeDifference = computed(() => {
  if (!event.value.eventEndDate) {
    return { days: 0, hours: 0 };
  }
  const endDate = new Date(event.value.eventEndDate);
  const currentDate = new Date(serverDateTime);
  const diffTime = Math.max(endDate.getTime() - currentDate.getTime(), 0);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  return { days: diffDays, hours: diffHours };
});

watch(timeDifference, (newVal) => {
  event.value.day = newVal.days;
  event.value.hours = newVal.hours;
});

onBeforeMount(() => {
  bullpassStore.getEventInfoWithAuth();
});

watch(userIsAuthorized, (authorized) => {
  if (authorized) {
    bullpassStore.getEventInfoWithAuth();
  }
});
</script>

<style lang="scss" module>
.author-btn {
  display: flex;
  height: 48px;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 16px;
  background: var(--button-filled-default);
  color: var(--general-background);
  transition: all 0.2s linear;
  margin-top: 14px;
  &:hover {
    background: var(--button-filled-hover);
  }
  &:active {
    background: var(--button-filled-active);
  }
  @media screen and (max-width: 800px) {
    height: 32px;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.12px;
    margin-top: 0;
  }
}
.home-slider-event {
  position: relative;
  display: flex;
  width: 324px;
  height: 320px;
  flex-shrink: 0;
  border-radius: 32px;
  background: radial-gradient(
    470.35% 473.06% at 210.49% -234.84%,
    #00f0ff 20.98%,
    #273696 81.83%
  );
  .event-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-image: url("/banners/bullpass-widget-desc.png");
  }

  .event-timer {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    height: 34px;
    padding: 6px 10px 6px 8px;
    align-items: center;
    border-radius: 24px;
    background: var(--general-transparent-light-5);
    backdrop-filter: blur(20px);
    pointer-events: none;
    .timer-wrapper {
      display: flex;
      gap: 6px;
      font-size: 14px;
      line-height: 21px;
    }
    @media screen and (max-width: 800px) {
      left: auto;
      transform: none;
      right: 12px;
      .time-to-end {
        font-size: 12px;
      }
    }
  }

  .event-info {
    position: absolute;
    display: flex;
    flex-direction: column;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 16px;
    border-radius: 28px;
    background: var(--general-transparent-dark-20);
    backdrop-filter: blur(20px);
    cursor: pointer;

    .lvl-name {
      display: flex;
      justify-content: center;
      text-align: center;
      gap: 8px;
      font-size: 18px;
      font-weight: 500;
      line-height: 36px;
      .lvl {
        width: 36px;
        height: 36px;
        border-radius: 12px;
        background: var(--general-transparent-light-2);
        box-shadow: 0px 0px 12px 0px rgba(255, 255, 255, 0.12) inset;
      }
      svg {
        width: 18px;
        height: 18px;
        fill: var(--general-white);
        transform: rotate(-90deg) translateX(3px);
      }
    }
    .lvl-progress {
      position: relative;
      margin-top: 16px;
      border-radius: 16px;
      background: var(--general-transparent-light-5);
      box-shadow:
        0px 0px 20px 0px rgba(255, 255, 255, 0.12) inset,
        0px 2px 8px 0px rgba(0, 0, 0, 0.05);

      .not-max-lvl {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        align-self: stretch;
        height: 44px;
        border-radius: 16px;
        span {
          width: 100%;
          text-align: center;
          font-size: 14px;
          line-height: 125%;
          letter-spacing: -0.14px;
        }
        .back-progress {
          position: absolute;
          background: var(--general-transparent-light-5);
          height: 100%;
          border-radius: 16px;
          transition: all 0.2s linear;
        }
      }
      .max-lvl {
        display: flex;
        width: 100%;
        text-align: center;
        color: var(--general-transparent-light-35);
        font-size: 14px;
        height: 44px;
        font-weight: 500;
        letter-spacing: -0.14px;
        p {
          margin: auto;
        }
      }
    }
    @media screen and (max-width: 800px) {
      border-radius: 18px;
      flex-direction: row;
      justify-content: space-between;
      height: 56px;
      padding: 12px;
      .lvl-name {
        font-size: 14px;
        svg {
          width: 14px;
          height: 14px;
          transform: rotate(-90deg) translateX(2px);
        }
      }
      .lvl-progress {
        width: 124px;
        margin-top: auto;
        border-radius: 10px;
        .not-max-lvl {
          height: 32px;
          .back-progress {
            border-radius: 6px;
          }
          span {
            font-size: 12px;
            line-height: 100%;
          }
        }
        .max-lvl {
          font-size: 12px;
          height: 32px;
          padding: 0px 10px;
          letter-spacing: -0.12px;
          font-weight: 400;
        }
      }
    }
  }

  @media screen and (max-width: 1000px) {
    width: 255px;
    height: 280px;
    border-radius: 20px;
    .event-img {
      background-image: url("/banners/bullpass-widget-tablet.png");
    }
  }
  @media screen and (max-width: 800px) {
    margin: auto;
    height: 200px;
    width: 420px;
    border-radius: 20px;
    .event-img {
      background-size: cover;
      background-image: url("/banners/bullpass-widget-mobile.png");
    }
  }
  @media screen and (max-width: 420px) {
    width: calc(100% - 24px);
    .event-img {
      border-radius: 20px;
    }
  }
}
.coming {
  display: flex;
  height: 44px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 16px;
  background: var(--general-transparent-light-5);
  box-shadow:
    0px 0px 20px 0px rgba(255, 255, 255, 0.12) inset,
    0px 2px 8px 0px rgba(0, 0, 0, 0.05);
  @media screen and (max-width: 800px) {
    font-size: 14px;
    border-radius: 10px;
    height: 32px;
    width: 100%;
  }
}
</style>
